// Authenticated composition (J10 wiring). Builds the call engine + services for
// the signed-in account, elects a single master tab that owns the WSS, mirrors
// service-worker / push / pending-invite plumbing, and mounts J9's screens under
// their provider. Torn down on unmount — which is exactly what sign-out/delete
// trigger, since AppRoot drops this subtree the moment the account goes away.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IdentityKeys } from '@merkury/brain/identity/keys.js';
import { ScreensProvider } from '../screens/index.js';
import { useIsDesktop } from '../screens/ui/AppShell.js';
import { useStore } from '../screens/ui/useStore.js';
import type { StoredAccount } from '@merkury/brain/storage/accountStore.js';
import { PendingInvitePickup, syncPushSubscription, type PushManagerLike } from '../push/index.js';
import { installAutoAccept } from '../push/pendingInvite.js';
import { installHiddenRing } from '../push/hiddenRing.js';
import { avatarNotificationIcon } from '../push/notificationAvatar.js';
import { getCachedFriends } from '../storage/serverCache.js';
import { SwController } from '../sw/index.js';
import { PUSH_ROTATE } from '../sw/messages.js';
import { installVisibilityReconciler, MasterTabGate, TabElection } from '../tabs/index.js';
import { useAccount } from './accountContext.js';
import { useT } from '../i18n/index.js';
import { App } from './App.js';
import { installSessionRevival } from './sessionRevival.js';
import { isCapacitorNative } from './capacitor/platform.js';
import { VAPID_PUBLIC_KEY } from './config.js';
import { createAppServices, refreshSignedEndpoints, type AppServices } from './services.js';

const IS_PROD = import.meta.env.PROD;

/** Long-lived singletons for one authenticated session (one services instance). */
interface SessionInfra {
  election: TabElection;
  pickup: PendingInvitePickup;
  sw: SwController;
  syncPush: () => Promise<void>;
}

function createSessionInfra(services: AppServices): SessionInfra {
  const { engine, rest } = services;

  const syncPush = async (): Promise<void> => {
    if (!VAPID_PUBLIC_KEY) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      await syncPushSubscription({
        // Platform boundary: the DOM PushManager satisfies our narrower seam.
        pushManager: registration.pushManager as unknown as PushManagerLike,
        vapidPublicKey: VAPID_PUBLIC_KEY,
        permission: () => Notification.permission,
        updateSession: (body) => rest.updateCurrentSession(body),
      });
    } catch {
      // Push is a nicety; never let its failure surface to the user.
    }
  };

  const pickup = new PendingInvitePickup({ ensureEngineStarted: () => engine.start() });

  const election = new TabElection({
    locks: navigator.locks,
    onLead: () => engine.start(),
    onDemote: () => engine.stop(),
  });

  const sw = new SwController({
    container: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    reload: () => window.location.reload(),
    onMessage: (data) => {
      if (pickup.handleSwMessage(data)) return;
      if (typeof data === 'object' && data !== null && (data as { type?: unknown }).type === PUSH_ROTATE) {
        void syncPush();
      }
    },
  });

  return { election, pickup, sw, syncPush };
}

/** The running authenticated app: services are built, everything is live. */
function AuthedRuntime({ services }: { services: AppServices }): ReactNode {
  const { engine } = services;
  const { getAccessToken, takePendingAvatarSeed } = useAccount();
  const navigate = useNavigate();

  // Global ringing watcher: an incoming invite routes to the incoming screen.
  // COMPACT ONLY — on desktop the shell's DesktopRingModal (App.tsx) overlays
  // whatever route is open, so no navigation happens for a ring.
  const isDesktop = useIsDesktop();
  const engineState = useStore(engine.state);
  const call = engineState.call;
  const ringing = call?.phase === 'ringing' && call.direction === 'incoming';
  useEffect(() => {
    if (ringing && !isDesktop) void navigate('/incoming');
  }, [ringing, isDesktop, navigate]);

  const infra = useMemo(() => createSessionInfra(services), [services]);

  // Fast cold-open ring (JW6): a pending invite from a notification/deep link
  // rings provisionally BEFORE the engine reconnects and re-receives the
  // invite — no loading screen between the tap and the ring. A 'decline'
  // decision needs no surface (installAutoAccept completes it silently).
  const pending = useStore(infra.pickup.state);
  const pendingRing = pending !== null && pending.action !== 'decline' && call === null;
  useEffect(() => {
    if (pendingRing && !isDesktop) void navigate('/incoming');
  }, [pendingRing, isDesktop, navigate]);

  // Screens read the pickup via context so the /incoming route (mounted by the
  // router with no props) still gets the provisional cold-open path.
  const screensDeps = useMemo(
    () => ({ ...services.screensDeps, pickup: infra.pickup, syncPush: infra.syncPush }),
    [services, infra],
  );

  useEffect(() => {
    const { election, pickup, sw, syncPush } = infra;
    const teardown: Array<() => void> = [];

    // Cold-open pending-invite pickup (notification deep link #invite=<callId>).
    pickup.consumeLocation(window.location, window.history);

    // Native Accept carries the decision (#invite=<id>&action=accept):
    // auto-answer the matching ringing call — never ask a second time.
    teardown.push(installAutoAccept(pickup, engine));

    // Join the master-tab election (leader → engine.start via onLead).
    void election.start();

    // iOS foreground reconciliation: the leader re-kicks its signaling client.
    teardown.push(
      installVisibilityReconciler({
        doc: document,
        election,
        onLeaderVisible: () => engine.start(),
      }),
    );

    // A server-revoked session must never be a dead end while the device
    // holds the identity key: silently re-mint and reconnect (backoff inside).
    teardown.push(installSessionRevival({ engine, getAccessToken }));

    // OS network changes (Wi-Fi ↔ LTE, online/offline) → let the engine react.
    const onNetworkChange = (): void => engine.noteNetworkChange();
    window.addEventListener('online', onNetworkChange);
    window.addEventListener('offline', onNetworkChange);
    teardown.push(() => {
      window.removeEventListener('online', onNetworkChange);
      window.removeEventListener('offline', onNetworkChange);
    });

    // Service worker + push only in production builds (dev has no built sw.js).
    // Push sync runs right after registration, and again on PUSH_ROTATE.
    if (IS_PROD && 'serviceWorker' in navigator) {
      void sw.register().then(() => syncPush());
    }

    // GAP-1 (JW8 audit §3): a page-side OS notification when a hidden-tab ring
    // arrives over the WSS and the push path is unavailable (push blocked but
    // permission granted — the censorship case). Same builder + `tag: callId`
    // as the SW push notification, so the two dedupe into one. Skipped on
    // Capacitor-native (Android has its own full-screen native ring).
    if ('serviceWorker' in navigator && !isCapacitorNative()) {
      teardown.push(
        installHiddenRing({
          engine,
          doc: document,
          getRegistration: () => navigator.serviceWorker.ready,
          // Personalize the hidden-tab ring from LOCAL data only — the cached
          // friends list, never the push payload (which stays name-free).
          resolvePeer: (peerUserId) => {
            const friend = getCachedFriends()?.data.find((f) => f.userId === peerUserId);
            if (!friend) return null;
            return {
              name: friend.displayName,
              ...(friend.avatarSeed !== undefined ? { avatarSeed: friend.avatarSeed } : {}),
            };
          },
          avatarIcon: (peerUserId, avatarSeed) => avatarNotificationIcon(peerUserId, avatarSeed),
        }),
      );
    }

    // Best-effort signed endpoint-list refresh for censorship resistance.
    void refreshSignedEndpoints(engine);

    // Avatar chosen during onboarding (JW11): the service persisted it
    // locally; PATCH it now that an authenticated REST client exists. Drained
    // once; a failure loses only the server copy (settings picker re-sends).
    void takePendingAvatarSeed()
      .then((seed) =>
        seed === undefined ? undefined : services.rest.updateMe({ avatarSeed: seed }),
      )
      .catch(() => undefined);

    // The Android native-UX wiring and the Compose bridge used to be installed
    // from here, behind `isCapacitorNative()`. Since B3-android the React tree
    // never runs inside the Android WebView at all — `brain.html`
    // (src/headless-android/main.ts) is the WebView's only document and owns
    // that wiring explicitly (docs/09 §2). This component is web-only now.

    return () => {
      for (const fn of teardown) fn();
      election.stop();
    };
  }, [infra, engine, services, getAccessToken, takePendingAvatarSeed]);

  return (
    <MasterTabGate election={infra.election}>
      <ScreensProvider deps={screensDeps}>
        <App />
      </ScreensProvider>
    </MasterTabGate>
  );
}

/**
 * Builds the authenticated services for `account`, then mounts the runtime.
 * Construction is async (it reads persisted endpoint state), so a brief loading
 * state is shown; disposal on unmount stops the engine and releases the mic.
 */
export function ReadyApp({
  account,
  identity,
}: {
  account: StoredAccount;
  identity: IdentityKeys;
}): ReactNode {
  const t = useT();
  const { getAccessToken, getKv } = useAccount();
  const [services, setServices] = useState<AppServices | null>(null);
  const builtRef = useRef<AppServices | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const kv = await getKv();
      const built = await createAppServices({ account, identity, getAccessToken, kv });
      if (cancelled) {
        built.dispose();
        return;
      }
      builtRef.current = built;
      setServices(built);
    })();
    return () => {
      cancelled = true;
      builtRef.current?.dispose();
      builtRef.current = null;
      setServices(null);
    };
    // Rebuild only when the identity behind the session changes; AppRoot keys
    // this component on userId so a re-key already remounts us cleanly. The
    // getAccessToken/getKv accessors are stable for a given AccountProvider.
  }, [account.userId, identity.publicKeyB64]);

  if (!services) {
    return (
      <main className="flex min-h-full items-center justify-center">
        <p className="text-muted" role="status">
          {t('common.loading')}
        </p>
      </main>
    );
  }
  return <AuthedRuntime services={services} />;
}

/**
 * Top of the app tree. Pre-auth (loading / onboarding) renders the plain route
 * table; once an account exists it mounts the full authenticated runtime, keyed
 * on the account id so a new identity rebuilds the session from scratch.
 *
 * `onboardingFlowActive` holds this on <App/> a beat longer: auth flips
 * `state.status` to 'ready' BEFORE the onboarding flow's post-auth
 * mic/notification priming steps run, and swapping to a freshly-keyed
 * <ReadyApp/> here would unmount that in-progress <OnboardingFlow/> (and any
 * state guarding it) before those steps ever get a chance to render or prompt.
 */
export function AppRoot(): ReactNode {
  const { state, onboardingFlowActive } = useAccount();
  if (state.status === 'ready' && !onboardingFlowActive) {
    return (
      <ReadyApp
        key={state.account.userId ?? 'pending'}
        account={state.account}
        identity={state.identity}
      />
    );
  }
  return <App />;
}
